/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021-2022 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { getTimelineData, IGetTimelineDataResponse } from '../../api/timeline';
import '../../styles/StatusTimeline.scss';
import '@fontsource/roboto';
import JobStatesColor from 'consts/JobStatesColor';

interface IPreparedTimelineDataArrayElement {
  name: string;
  times: {
    // d3 require these keys
    starting_time: Date;
    ending_time: Date;
  }[];
}

interface IPreparedTimelineData {
  states: IPreparedTimelineDataArrayElement[];
  tMax: Date;
  tMin: Date;
}

interface IStatusTimelineState extends IGetTimelineDataResponse {
  timestamps?: IPreparedTimelineData[];
}

function useOnScreen(ref) {
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting));

  useEffect(() => {
    if (ref.current) observer.observe(ref.current);
    // Remove the observer as soon as the component is unmounted
    return () => {
      observer.disconnect();
    };
  });

  return isIntersecting;
}

interface StatusTimelineProps {
  namespaceUri: string;
  colorMap?: { [key: string]: string };
}

type Timeout = ReturnType<typeof setTimeout>;

const StatusTimeline = (props: StatusTimelineProps) => {
  const [timeData, setTimeData] = useState<IPreparedTimelineData>();
  const [ltimer, lsetTimer] = useState<Timeout>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isVisible = useOnScreen(canvasRef);

  const colorMap = props.colorMap || JobStatesColor;

  const formatQueryStringTime = (date) => {
    return ''
      .concat(('0000' + date.getUTCFullYear()).substr(-4))
      .concat(('0000' + (date.getUTCMonth() + 1)).substr(-2))
      .concat(('0000' + date.getUTCDate()).substr(-2))
      .concat(('0000' + date.getUTCHours()).substr(-2))
      .concat(('0000' + date.getUTCMinutes()).substr(-2));
  };

  const getQuery = (machineId, isInitialRequest: boolean, timeOfLastRequest: Date | undefined) => {
    const to = new Date();
    const from = isInitialRequest ? new Date(to) : timeOfLastRequest;

    if (isInitialRequest) {
      from?.setMinutes(from.getMinutes() - 30);
    }

    return {
      params: {
        machineId,
        from: formatQueryStringTime(from),
        to: formatQueryStringTime(to),
      },
    };
  };

  const prepareData = (
    timedData: IStatusTimelineState,
    isInitialRequest: boolean,
    states: IPreparedTimelineDataArrayElement[] | undefined,
  ) => {
    // When there are no values for OFFLINE_THRESHOLD_MS, the machine is seen as offline and there is a gap in the timeline
    const OFFLINE_THRESHOLD_MS = 60 * 1000; // in ms
    const orderedValues = Object.keys(timedData.values)
      .sort()
      .map((key /* , index */) => {
        return {
          time: key.replace(' ', 'T') + 'Z',
          value: timedData.values[key],
        };
      });

    const valueToKeyMap: { [value: string]: number } = {};
    let index = 0;
    for (const key of Object.keys(colorMap)) {
      valueToKeyMap[key] = index++;
    }

    let timestamps: IPreparedTimelineDataArrayElement[] = Object.keys(valueToKeyMap).map((key) => {
      return { name: key, times: [] };
    });
    if (!isInitialRequest && states) {
      timestamps = states;
    }

    let lastState = '-1';
    let lastStartTime: Date;
    let lastEndTime: Date;

    orderedValues.forEach((el, index) => {
      const currentTime = new Date(el.time);
      const currentState = el.value;
      if (index === 0) {
        lastState = currentState;
        lastStartTime = new Date(currentTime);
        lastEndTime = currentTime;
      }
      const timeTooLong = +currentTime - +lastEndTime > OFFLINE_THRESHOLD_MS;
      const key = valueToKeyMap[lastState];

      if (index < orderedValues.length - 1) {
        if (lastState !== currentState || timeTooLong) {
          if (!timeTooLong) {
            lastEndTime = currentTime;
          }
          if (!timestamps[key]?.times) {
            return;
          }
          timestamps[key].times.push({ starting_time: lastStartTime, ending_time: lastEndTime });
          lastState = currentState;
          lastStartTime = timeTooLong ? new Date(currentTime) : new Date(lastEndTime);
        }
        lastEndTime = currentTime;
      } else if (!timeTooLong) {
        // Insert last value
        timestamps[key]?.times?.push({ starting_time: lastStartTime, ending_time: lastEndTime });
      }
    });

    const now = new Date();
    const then = new Date(now);

    // Dummy Data for identical x-Axis-Ticks in machineList

    then.setMinutes(now.getMinutes() - 60);
    timestamps[0]?.times.push({ starting_time: then, ending_time: then });
    timestamps[0]?.times.push({ starting_time: now, ending_time: now });

    const newCurrentTimeData: IPreparedTimelineData = {
      states: timestamps,
      tMax: now,
      tMin: then,
    };

    setTimeData(newCurrentTimeData);
  };

  function loadData(
    namespaceUri: string,
    isInitialRequest: boolean,
    timeOfLastRequest: Date | undefined = undefined,
    states: IPreparedTimelineDataArrayElement[] | undefined = undefined,
  ) {
    if (namespaceUri) {
      getTimelineData(getQuery(namespaceUri, isInitialRequest, timeOfLastRequest))
        .then((rep) => {
          prepareData(rep, isInitialRequest, states);
        })
        .catch();
    }
  }

  useEffect(() => {
    loadData(props.namespaceUri, true);
    let date = new Date();
    clearInterval(ltimer);
    const REFRESH_INTERVAL_MS = 5 * 1000; // 20 Seconds
    const interval = setInterval(() => {
      if (isVisible) {
        loadData(props.namespaceUri, false, date, timeData?.states);
        date = new Date();
      }
    }, REFRESH_INTERVAL_MS);
    lsetTimer(interval);
    return () => clearInterval(interval);
  }, [isVisible, props.namespaceUri]);

  useEffect(() => {
    const draw = (ctx: CanvasRenderingContext2D, cvs: HTMLCanvasElement, data: IPreparedTimelineData) => {
      if (!data.states) return;
      cvs.width = cvs.getBoundingClientRect().width * 3;
      cvs.height = cvs.getBoundingClientRect().height * 3;

      const xMax = cvs.width;
      const yMax = cvs.height;
      const tMin = data.tMin;

      const xPercToPx = (percent: number) => (xMax * percent) / 100;
      const yPercToPx = (percent: number) => (yMax * percent) / 100;
      const dateToPerc = (date: Date) => (date.getTime() - tMin.getTime()) / 10 / 3600;
      const dateToPx = (date: Date) => xPercToPx(dateToPerc(date));

      const paddingTopPercent = 5;
      const heightOfTimelinePercent = 50;
      const FIVE_MINUTES_IN_MS = 1000 * 60 * 5;

      const fillStyle = 'gray';
      const fontSizePercentY = 45;
      const lineWidthPercentX = 0.1;
      const verticalMarkerStartPercentY = 62;
      const verticalMarkerStopPercentY = 57;

      for (const timestamps of data.states) {
        const color = colorMap[timestamps.name];
        for (const timestamp of timestamps.times) {
          const rect = {
            x: dateToPx(timestamp.starting_time),
            y: yPercToPx(paddingTopPercent),
            width: dateToPx(timestamp.ending_time) - dateToPx(timestamp.starting_time),
            height: yPercToPx(heightOfTimelinePercent),
          };
          ctx.beginPath();
          ctx.fillStyle = color;
          ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
          ctx.stroke();
        }
      }

      ctx.font = `${yPercToPx(fontSizePercentY)}px Roboto`;
      ctx.fillStyle = fillStyle;
      ctx.strokeStyle = fillStyle;
      ctx.lineWidth = xPercToPx(lineWidthPercentX);

      const tMinRounded = new Date(Math.round(tMin.getTime() / FIVE_MINUTES_IN_MS) * FIVE_MINUTES_IN_MS);
      for (let i = 0; i < 13; i++) {
        const time = new Date(tMinRounded.getTime() + i * FIVE_MINUTES_IN_MS);
        const leftShiftedTime = new Date(tMinRounded.getTime() + (i - 0.33) * FIVE_MINUTES_IN_MS);
        const rightShiftedTime = new Date(tMinRounded.getTime() + (i + 0.33) * FIVE_MINUTES_IN_MS);
        //                   |THIS IS THE TEXT
        // <----- xPosText ->|
        const xPosText = dateToPx(leftShiftedTime);
        //               THIS IS THE TEXT|
        // <----- xPosTextRight -------->|
        const xPosTextRight = dateToPerc(rightShiftedTime);
        // Check if text is out of bounds
        if (xPosText > 0 && xPosTextRight < 100) {
          const hours = String(time.getHours()).padStart(2, '0');
          const minutes = String(time.getMinutes()).padStart(2, '0');
          ctx.fillText(`${hours}:${minutes}`, xPosText, yPercToPx(100));
        }
        ctx.beginPath();
        ctx.moveTo(dateToPx(time), yPercToPx(verticalMarkerStartPercentY));
        ctx.lineTo(dateToPx(time), yPercToPx(verticalMarkerStopPercentY));
        ctx.stroke();
      }
    };
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas && context && timeData && isVisible) {
      draw(context, canvas, timeData);
      window.addEventListener('resize', () => draw(context, canvas, timeData));
    }
  }, [timeData, isVisible, colorMap]);

  const numElements = timeData?.states?.reduce((acc, el) => {
    return acc + el.times.length;
  }, 0);
  // There are always 2 Elements, to ensure a fixe size.
  const hasTimeData = numElements === undefined || numElements > 2;
  return (
    <div className="status-timeline">
      {!timeData && <Alert variant="info">Loading... This may take a few seconds!</Alert>}
      {timeData && !hasTimeData && <Alert variant="info">No timeline data.</Alert>}
      {hasTimeData && <canvas ref={canvasRef} />}
    </div>
  );
};

export default StatusTimeline;
